// src/hooks/useCompanyActivityLogger.js

import DOMPurify from "dompurify";
import api from "../services/api/axios";
import { useGraph } from "../app/auth/useGraph";

/**
 * useCompanyActivityLogger
 *
 * Logs Outlook email activity to the backend.
 *
 * Options:
 * - companyId (number): force-associate the email with this company_id
 */
export function useCompanyActivityLogger({ companyId } = {}) {
  const { graphGet } = useGraph();

  async function logEmailById(messageId) {
    try {
      const encodedMessageId = encodeURIComponent(messageId);
      // --------------------------------------------------
      // 1️⃣ Fetch full message from Microsoft Graph
      // --------------------------------------------------
      const data = await graphGet(
        `/me/messages/${encodedMessageId}?$expand=attachments`,
      );

      // --------------------------------------------------
      // 2️⃣ Sanitize HTML body
      // --------------------------------------------------
      const cleanHtml = DOMPurify.sanitize(data?.body?.content || "");

      // --------------------------------------------------
      // 3️⃣ Build recipients array
      // --------------------------------------------------
      const recips = [];

      if (data?.from?.emailAddress?.address) {
        recips.push({
          rtype: "from",
          name: data.from.emailAddress.name || null,
          email: data.from.emailAddress.address,
        });
      }

      (data?.toRecipients || []).forEach((r) =>
        recips.push({
          rtype: "to",
          name: r.emailAddress?.name || null,
          email: r.emailAddress?.address || null,
        }),
      );

      (data?.ccRecipients || []).forEach((r) =>
        recips.push({
          rtype: "cc",
          name: r.emailAddress?.name || null,
          email: r.emailAddress?.address || null,
        }),
      );

      // --------------------------------------------------
      // 4️⃣ Determine direction (sent vs received)
      // --------------------------------------------------
      const me = await graphGet("/me?$select=mail,userPrincipalName");

      const myEmail = (me?.mail || me?.userPrincipalName || "").toLowerCase();
      const fromEmail = (data?.from?.emailAddress?.address || "").toLowerCase();

      const direction =
        fromEmail && myEmail && fromEmail === myEmail ? "sent" : "received";

      // --------------------------------------------------
      // 5️⃣ Send email activity to backend
      // --------------------------------------------------
      await api.post("/company-activity/email/upsert", {
        company_id: companyId || null,

        from: {
          email: data?.from?.emailAddress?.address || null,
          name: data?.from?.emailAddress?.name || null,
        },

        external_id: data?.id || null,
        internet_message_id: data?.internetMessageId || null,
        conversation_id: data?.conversationId || null,

        subject: data?.subject || null,
        occurred_at: data?.receivedDateTime || data?.sentDateTime || null,
        direction,

        body_html: cleanHtml,
        body_text: "",

        recipients: recips,
        attachments: [],

        visibility: "org",
      });

      // --------------------------------------------------
      // 6️⃣ Return message to caller
      // --------------------------------------------------
      return data;
    } catch (err) {
      console.error("❌ Email activity logging failed:", err);
      throw err;
    }
  }

  return { logEmailById };
}
